import { useCallback, useEffect } from 'react';
import { toString } from 'lodash';

import { FileImage } from '@/common/constants';
import prepareFile from '@/components/controls/DropScan/prepareFile';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { IDocumentFilesUrlDto } from '@/dtos';
import useFilesStore from '@/modules/transaction/components/ReimburseMe/store/useFiles.store';
import { useQRCodeStore } from '@/modules/transaction/components/ReimburseMe/store/useQRCode.store';
import { getBlob } from '@/modules/transaction/hooks/useFileUploadQuery';
import useGetDocumentByQRCodeQuery from '@/modules/transaction/hooks/useGetDocumentByQRCode.query';
import { getFileNameFromPath, safeDecodeURIComponent } from '@/utils/modifiers';

import { useQrTransactionFileUploadAdditionalActionsStore } from '../../TransactionDetails/TransactionFileUploadPopup/store';
import { useQrFileUploadAdditionalActionsStore } from '../FileUploadPopup/store';

interface Props {
  storeKey?: string;
  onSetFileFromStream?: () => void;
}

const useSetFileFromDocumentStream = ({ storeKey, onSetFileFromStream }: Props) => {
  const { state: { qrCodeId }, handleReset } = useQRCodeStore();
  const { document } = useGetDocumentByQRCodeQuery({ qrCodeId });
  const { files, setFiles } = useFilesStore((state) => state);
  const additionalAction = useQrFileUploadAdditionalActionsStore((state) => state.additionalAction);
  const resetFileStore = useQrFileUploadAdditionalActionsStore((state) => state.resetFileStore);

  const additionalTransactionAction = useQrTransactionFileUploadAdditionalActionsStore((state) => state.additionalAction);
  const resetTransactionFileStore = useQrTransactionFileUploadAdditionalActionsStore((state) => state.resetFileStore);
  const {
    handleSetValues,
    values: { files: documentFiles, documentId },
  } = useFilesInDocumentStore(storeKey);
  const handleSetQRCodeStore = useQRCodeStore((state) => state.setState);

  const handleCreateDocument = useCallback(async () => {
    const createdDocumentId = document?.document_id;
    handleSetQRCodeStore({
      documentId: toString(createdDocumentId),
      qrCodeId,
    });
  }, [document, handleSetQRCodeStore, qrCodeId]);


  const handleSetFilesFromStream = useCallback((filesFromStream: FileImage) => {
    if (additionalAction) {
      additionalAction();
    }
    if (additionalTransactionAction) {
      additionalTransactionAction();
    }
    if (!documentId) {
      handleSetValues({ documentId: toString(document?.document_id) });
    }
    handleCreateDocument();
    setFiles([
      ...resetFileStore || resetTransactionFileStore ? [] : files,
      filesFromStream
    ]);
    handleSetValues({ files: [...documentFiles, filesFromStream] });
    onSetFileFromStream?.();
  }, [
    documentId,
    handleCreateDocument,
    setFiles,
    files,
    handleSetValues,
    documentFiles,
    document,
    resetFileStore,
    additionalAction,
    resetTransactionFileStore,
    additionalTransactionAction,
    onSetFileFromStream,
  ]);

  const handleSetFileFromDocumentStream = useCallback(async (value: IDocumentFilesUrlDto) => {
    const fileUrl = value?.files?.[0]?.s3_url;
    const fileId = toString(value?.files?.[0]?.id);
    const currentFileName = getFileNameFromPath(fileUrl);
    const decodedFileName = safeDecodeURIComponent(currentFileName);

    if (!fileUrl || !fileId) return;

    const isFileAlreadyExist = files.find((file) => file.id === fileId);

    if (!isFileAlreadyExist) {
      const fileBlob = await getBlob(fileUrl);

      const fileImage = prepareFile({
        id: fileId,
        name: decodedFileName || '',
        temporalId: fileId,
        url: fileUrl,
        type: fileBlob.type,
        blob: fileBlob,
      });

      handleSetFilesFromStream(fileImage);
    }
  }, [handleSetFilesFromStream, files]);

  useEffect(() => {
    if (document) {
      handleSetFileFromDocumentStream(document);
    }
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [document]);

  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  useEffect(() => () => handleReset(), []);
};

export default useSetFileFromDocumentStream;
